import React, { useState, useEffect, useRef } from 'react';
import {
    TrendingDown, Plus, GripVertical, X, FileText, Loader2, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SearchBar } from '@/components/SearchBox/SearchBar';
import { useLanguage } from '../../../context/LanguageContext';
import { SearchBarPagination } from '@/components/SearchBox/SearchBarPagination';
import { ENTITY_SUGGESTION_TYPES, getEntitySuggestionSearchAPI } from '@/redux/Common/actions';

// Factory function to create empty AP charge row

export const createEmptyAPChargeRow = () => ({
    rowid: crypto.randomUUID(), // Unique row ID for React key
    id: 0,
    chargeCode: '',
    description: '',
    currency: 'USD',
    roe: '1.000000',
    basis: '',
    container: '',
    quantity: '1',
    vendor: '',
    vendorId: 0,
    vendorRef: '',
    amount: '',
    buyRate: '',
    ppcc: 'P',
    billStatus: 'not_billed',
    billId: null,
    billNo: '',
});

// Sortable row component
const SortableRow = ({ charge, index, children }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: charge.rowid });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 'auto',
    };

    return (
        <tr
            ref={setNodeRef}
            style={style}
            className={`border-b border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${isDragging ? 'bg-slate-100 dark:bg-slate-600' : ''
                }`}
        >
            <td className="px-2 py-2 w-8">
                <button
                    {...attributes}
                    {...listeners}
                    className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-200 dark:hover:bg-slate-600 rounded"
                    title="Drag to reorder"
                >
                    <GripVertical className="w-4 h-4 text-slate-400" />
                </button>
            </td>
            {children}
        </tr>
    );
};

const APChargesTable = ({
    charges,
    setCharges,
    currencyList = [],
    basisList = [],
    containerList = [],
    chargeCodeList = [],
    onSyncToBill,
    isSyncing = false,
    bookingId,
    totalbill
}) => {
    const { t } = useLanguage();
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );
    const rowIdInitialized = useRef(false);

    useEffect(() => {
        if (rowIdInitialized.current) return;

        if (!charges || charges.length === 0) return;

        const updated = charges.map((c) => ({
            ...c,
            rowid: c.rowid || crypto.randomUUID()
        }));

        rowIdInitialized.current = true;

        setCharges(updated);


    }, [charges]);


    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (!over || active.id === over.id) return;

        const oldIndex = charges.findIndex((c) => c.rowid === active.id);
        const newIndex = charges.findIndex((c) => c.rowid === over.id);

        setCharges(arrayMove(charges, oldIndex, newIndex));
    };

    const addCharge = () => {
        setCharges([...charges, createEmptyAPChargeRow()]);
    };

    const removeCharge = (rowid) => {
        console.log('Removing charge with rowid:', rowid);
        setCharges(charges.filter((c) => c.rowid !== rowid));
    };

    const updateCharge = (rowid, field, value) => {
        setCharges(prevCharges => {
            const updatedCharges = prevCharges.map((c) => {
                if (c.rowid === rowid) {
                    const updated = { ...c, [field]: value };
                    if (field === 'amount' || field === 'quantity' || field === 'roe') {
                        const amount = parseFloat(updated.amount) || 0;
                        const qty = parseFloat(updated.quantity) || 1;
                        const roe = parseFloat(updated.roe) || 1;
                        updated.buyRate = ((amount * qty) / roe).toFixed(2);
                    }
                    return updated;
                }
                return c;
            });
            return updatedCharges;
            console.log('Updated charges after change:', updatedCharges);
        });
    };

    const getStatusBadge = (charge) => {
        const status = charge.billStatus;
        const billNo = charge.billNo;

        switch (status) {
            case 'billed':
            case 'approved':
            case 'paid':
                return (
                    <div className="flex items-center gap-1">
                        <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">
                            {billNo || 'Billed'}
                        </span>
                        {billNo && (
                            <a
                                href={`/accounting/bills`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-0.5 hover:bg-green-100 rounded"
                                title="View Bill"
                            >
                                <ExternalLink className="w-3 h-3 text-green-600" />
                            </a>
                        )}
                    </div>
                );
            case 'draft':
                return (
                    <div className="flex items-center gap-1">
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-600 rounded">
                            {billNo || 'Draft'}
                        </span>
                        {billNo && (
                            <a
                                href={`/accounting/bills`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-0.5 hover:bg-blue-100 rounded"
                                title="View Bill"
                            >
                                <ExternalLink className="w-3 h-3 text-blue-600" />
                            </a>
                        )}
                    </div>
                );
            case 'not_billed':
            default:
                return <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-600 rounded block">{t('notBilled')}</span>;
        }
    };

    const totalAP = charges.reduce((sum, c) => {
        const amount = parseFloat(c.amount) || 0;
        const qty = parseFloat(c.quantity) || 1;
        return sum + (amount * qty);
    }, 0);

    const inputClass = "w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 focus:ring-1 focus:ring-green-500 focus:border-green-500";
    const selectClass = "w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 focus:ring-1 focus:ring-green-500";

    return (
        <div className="space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-rose-500" />
                    <h3 className="text-sm font-medium text-white bg-orange-500 px-2 py-1 rounded">
                        {t('accountsPayable')} </h3>
                    <span className='text-xs text-gray-600'> ({t('costs')})</span>

                </div>
                <div className="flex items-center gap-2">
                    {onSyncToBill && bookingId && bookingId !== 0 && charges.length && totalbill > 0 && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onSyncToBill}
                            disabled={isSyncing}
                            className="text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
                            data-testid="sync-ap-to-bill-btn"
                        >
                            {isSyncing ? (
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                                <FileText className="w-3 h-3 mr-1" />
                            )}
                            {t('syncToBill')}
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={addCharge}
                        className="flex items-center border border-orange-400 rounded-lg hover:bg-orange-400 dark:hover:bg-orange-400 text-xs"
                        data-testid="add-ap-charge-btn"
                    >
                        <Plus className="w-3 h-3 mr-1" /> {t('addAPCharge')}
                    </Button>
                </div>
            </div>

            {/* Table */}
            <div className="border rounded-lg overflow-auto">
                <table className="w-full text-xs text-slate-700 dark:text-slate-300">
                    <thead>
                        <tr className="bg-gray-100 dark:bg-slate-700/50">
                            <th className="px-2 py-2 w-8"></th>
                            <th className="px-2 py-2 text-left font-medium w-12">SNO</th>
                            <th className="px-2 py-2 text-left font-medium min-w-[180px]">CHARGE DESCRIPTION</th>
                            <th className="px-2 py-2 text-center font-medium w-16">CURR</th>
                            <th className="px-2 py-2 text-center font-medium w-20">ROE</th>
                            <th className="px-2 py-2 text-center font-medium w-20">BASIS</th>
                            <th className="px-2 py-2 text-center font-medium w-20">CONTAINER</th>
                            <th className="px-2 py-2 text-center font-medium w-14">QTY</th>
                            <th className="px-2 py-2 text-left font-medium min-w-[140px]">VENDOR</th>
                            <th className="px-2 py-2 text-left font-medium w-20">REF #</th>
                            <th className="px-2 py-2 text-right font-medium w-24">AMOUNT</th>
                            <th className="px-2 py-2 text-right font-medium w-24">BUY RATE</th>
                            <th className="px-2 py-2 text-center font-medium w-16">PP/CC</th>
                            <th className="px-2 py-2 text-center font-medium w-20">STATUS</th>
                            <th className="px-2 py-2 text-center font-medium w-10">DEL</th>
                        </tr>
                    </thead>
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={charges.map((c) => c.rowid)}
                            strategy={verticalListSortingStrategy}
                        >
                            <tbody>
                                {charges.length === 0 ? (
                                    <tr>
                                        <td colSpan={16} className="px-4 py-8 text-center text-slate-500">
                                            {t('noAPCharges')}
                                        </td>
                                    </tr>
                                ) : (
                                    charges.map((charge, index) => (
                                        <SortableRow key={index + 1} charge={charge} index={index}>
                                            <td className="px-2 py-2 text-center font-medium text-slate-600">
                                                {index + 1}
                                            </td>
                                            <td className="px-1 py-1 min-w-[180px]">
                                                <SearchBarPagination
                                                    searchAPI={'v1/Common/ChargesDescription_List?flag=0&searchKeyword='}
                                                    defaultSearch="o"
                                                    selectReacord={(d) => {
                                                        updateCharge(charge.rowid, 'chargeCode', d?.[0]?.toString() || '');
                                                        updateCharge(charge.rowid, 'description', d?.[1] || '');
                                                    }}

                                                    value={charge.description || ''}
                                                    onchange={(val) => {
                                                        if (!val) {
                                                            updateCharge(charge.rowid, 'chargeCode', '');
                                                            updateCharge(charge.rowid, 'description', '');
                                                        }
                                                    }}
                                                    renderItem={(item) => item.chargesdescription}
                                                />
                                            </td>
                                            <td className="px-1 py-1">
                                                <select
                                                    value={charge.currency}
                                                    onChange={(e) => updateCharge(charge.rowid, 'currency', e.target.value)}
                                                    className={selectClass}
                                                >
                                                    <option value="USD">USD</option>
                                                    {currencyList?.map((c, i) => (
                                                        <option key={i} value={c.currencycode}>{c.currencycode}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="px-1 py-1">
                                                <Input
                                                    type="number"
                                                    step="0.000001"
                                                    value={charge.roe}
                                                    onChange={(e) => updateCharge(charge.rowid, 'roe', e.target.value)}
                                                    className={`${inputClass} text-center`}
                                                />
                                            </td>
                                            <td className="px-1 py-1">
                                                <select
                                                    value={charge.basis}
                                                    onChange={(e) => updateCharge(charge.rowid, 'basis', e.target.value)}
                                                    className={selectClass}
                                                >
                                                    <option value="">Select</option>

                                                    {basisList?.map((b, i) => (
                                                        <option key={i} value={b.chargebasis}>{b.chargebasis}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="px-1 py-1">
                                                <select
                                                    value={charge.container}
                                                    onChange={(e) => updateCharge(charge.rowid, 'container', e.target.value)}
                                                    className={selectClass}
                                                >
                                                    <option value="">Select</option>

                                                    {containerList?.map((c, i) => (
                                                        <option key={i} value={c.moveType}>{c.moveType}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="px-1 py-1">
                                                <Input
                                                    type="number"
                                                    min="1"
                                                    value={charge.quantity}
                                                    onChange={(e) => updateCharge(charge.rowid, 'quantity', e.target.value)}
                                                    className={`${inputClass} text-center w-14`}
                                                />
                                            </td>
                                            <td className="px-1 py-1">
                                                <div className="flex items-center gap-1">
                                                    <div className="flex-1">
                                                        <SearchBarPagination
                                                            searchAPI={getEntitySuggestionSearchAPI(ENTITY_SUGGESTION_TYPES.VENDOR)}
                                                            defaultSearch="m"
                                                            selectReacord={(d) => {
                                                                updateCharge(charge.rowid, 'vendor', d?.[1]);
                                                                updateCharge(charge.rowid, 'vendorId', d?.[0]);
                                                            }}
                                                            value={charge.vendor || ''}
                                                            onchange={(val) => {
                                                                if (!val) {
                                                                    updateCharge(charge.rowid, 'vendor', '');
                                                                    updateCharge(charge.rowid, 'vendorId', 0);
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    {charge.vendor && (
                                                        <button
                                                            onClick={() => {
                                                                updateCharge(charge.rowid, 'vendor', '');
                                                                updateCharge(charge.rowid, 'vendorId', 0);
                                                            }}
                                                            className="p-1 hover:bg-red-50 rounded text-red-500"
                                                        >
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-1 py-1">
                                                <Input
                                                    type="text"
                                                    value={charge.vendorRef || ''}
                                                    onChange={(e) => updateCharge(charge.rowid, 'vendorRef', e.target.value)}
                                                    className={inputClass}
                                                    placeholder="Ref #"
                                                />
                                            </td>
                                            <td className="px-1 py-1">
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    value={charge.amount}
                                                    onChange={(e) => updateCharge(charge.rowid, 'amount', e.target.value)}
                                                    className={`${inputClass} text-right`}
                                                    placeholder="0.00"
                                                />
                                            </td>
                                            <td className="px-1 py-1">
                                                <Input
                                                    type="text"
                                                    value={charge.buyRate || '0.00'}
                                                    readOnly
                                                    className={`${inputClass} text-right bg-slate-50 dark:bg-slate-900`}
                                                />
                                            </td>
                                            <td className="px-1 py-1">
                                                <select
                                                    value={charge.ppcc}
                                                    onChange={(e) => updateCharge(charge.rowid, 'ppcc', e.target.value)}
                                                    className={selectClass}
                                                >
                                                    <option value="P">P</option>
                                                    <option value="C">C</option>
                                                </select>
                                            </td>
                                            <td className="px-2 py-1 text-center">

                                                {getStatusBadge(charge)}

                                            </td>
                                            <td className="px-2 py-1 text-center">
                                                <button
                                                    onClick={() => removeCharge(charge.rowid)}
                                                    className="p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded text-red-500"
                                                    title="Delete charge"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </SortableRow>
                                    ))
                                )}
                            </tbody>
                        </SortableContext>
                    </DndContext>
                </table>
            </div>

            {/* Total */}
            {/* <div className="flex justify-end"> */}
            {/* <div className="rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/30">
    <div className="text-[11px] uppercase tracking-wide text-red-600 dark:text-red-400">{t('totalAP')} (USD)</div>
    <div className="mt-1 text-[20px] font-semibold text-red-700 dark:text-red-300 tabular-nums">
      ${totalAP.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </div>
  </div>
</div> */}
            <div className="mt-3 rounded-xl bg-gradient-to-r from-rose-50 to-rose-50/30 border border-rose-200/60 px-4 py-3 flex items-center justify-end dark:from-emerald-950/40 dark:to-emerald-900/20 dark:border-emerald-700/60">
                <span className="text-[12px]  text-rose-800 mr-2 dark:text-emerald-300">
                    {t('totalAP')} (USD)
                </span>
                <span className="text-[15px] font-semibold  text-rose-800 tabular-nums dark:text-emerald-200">
                    ${totalAP.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
            </div>
        </div>
    );
};

export default APChargesTable;
